import { RedoListItem } from "@redotech/arbiter/list/redo-list";
import { RedoListDropdown } from "@redotech/arbiter/list/redo-list-dropdown";
import { useSearch } from "@redotech/react-util/search";
import { useTranslation } from "@redotech/redo-i18n";
import PlusIcon from "@redotech/redo-icons/plus.svg";
import Fuse from "fuse.js";
import { memo, useMemo, useState } from "react";
import { TeamSwitcherData } from "./team-switcher-data";

const ADD_TEAM_ID = "add-team";

interface TeamSwitcherDropdownProps {
  teamData: TeamSwitcherData;
  dropdownAnchor: HTMLElement | null;
  dropdownOpen: boolean;
  setDropdownOpen: (open: boolean) => void;
  /** Suppresses the ticket badge on stores with nothing waiting. */
  hideZeroTicketBadge?: boolean;
  onTeamSelected?: (teamId: string) => void;
}

export const TeamSwitcherDropdown = memo(function TeamSwitcherDropdown({
  teamData,
  dropdownAnchor,
  dropdownOpen,
  setDropdownOpen,
  hideZeroTicketBadge = false,
  onTeamSelected,
}: TeamSwitcherDropdownProps) {
  const { t } = useTranslation();
  const [searchText, setSearchText] = useState("");
  const { currentTeam, connectedStores, setCurrentTeam, addStore } = teamData;

  const searchableTeamItems: RedoListItem<string>[] = useMemo(() => {
    const currentTeamItem: RedoListItem<string> = {
      id: currentTeam.teamId,
      value: currentTeam.teamId,
      type: "text",
      text: currentTeam.name,
      leadingItem: {
        type: "avatar",
        imageUrl: currentTeam.logoUrl,
        alt: currentTeam.name,
        name: currentTeam.name,
      },
    };

    const connectedTeams: RedoListItem<string>[] = connectedStores.map(
      (connectedStore) => ({
        id: connectedStore.teamId,
        value: connectedStore.teamId,
        type: "text",
        text: connectedStore.name,
        infoBadge:
          hideZeroTicketBadge && connectedStore.ticketCount === 0
            ? undefined
            : {
                type: "alternate",
                text: connectedStore.ticketCount,
                color: connectedStore.ticketCount > 0 ? "error" : "black",
              },
        leadingItem: {
          type: "avatar",
          imageUrl: connectedStore.logoUrl,
          alt: connectedStore.name,
          name: connectedStore.name,
        },
      }),
    );

    return [currentTeamItem, ...connectedTeams];
  }, [currentTeam, connectedStores, hideZeroTicketBadge]);

  const fuseSearcher = useMemo(() => {
    return new Fuse(searchableTeamItems, { keys: ["text"], threshold: 0.2 });
  }, [searchableTeamItems]);

  const filteredTeams = useSearch(
    fuseSearcher,
    searchableTeamItems,
    searchText,
  );

  const teamSwitcherList: RedoListItem<string>[] = useMemo(() => {
    const addTeam: RedoListItem<string> = {
      id: ADD_TEAM_ID,
      type: "text",
      text: t("Add another store", { context: "team switcher" }),
      leadingItem: { type: "icon", Icon: PlusIcon },
      value: ADD_TEAM_ID,
    };

    return [...filteredTeams, addTeam];
  }, [filteredTeams, t]);

  return (
    <RedoListDropdown
      darkMode
      dropdownAnchor={dropdownAnchor}
      dropdownOpen={dropdownOpen}
      items={teamSwitcherList}
      itemSelected={({ item }) => {
        if (item.id === ADD_TEAM_ID) {
          addStore();
          return;
        }
        onTeamSelected?.(item.value);
        setCurrentTeam(item.value);
      }}
      searchPlaceholder={t("Search stores...", { context: "team switcher" })}
      searchString={searchText}
      selectedItems={currentTeam.teamId}
      setDropdownOpen={(open) => {
        setDropdownOpen(open);
        if (!open) {
          setSearchText("");
        }
      }}
      setSearchString={setSearchText}
      size="sm"
    />
  );
});
